/**
=========================================================
* Transcript Analysis Component
=========================================================

* This component provides an exploration-based interface for analysing
* transcript data, including word frequencies, phrases, charts, and word clouds.
*/

import { useMemo, useState } from 'react'

import { Autocomplete, Tab, Tabs } from '@mui/material'
import Card from '@mui/material/Card'
import Icon from '@mui/material/Icon'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'
import { useTranscriptAnalysis, useTranscriptComparison } from 'hooks/useAnalysis'
import LoadingComponent from 'modules/Loading'

import ComparisonTab from './ComparisonTab'
import OverviewTab from './OverviewTab'
import PhrasesTab from './PhrasesTab'
import { PhraseSortField, SortDirection, WordSortField } from './types'
import WordsTab from './WordsTab'

type TimeFrameOption = {
	label: string
	days: number
}

type TabValue = 'overview' | 'words' | 'phrases' | 'comparison'

const TIME_FRAME_OPTIONS: TimeFrameOption[] = [
	{ label: 'Last 7 Days', days: 7 },
	{ label: 'Last 14 Days', days: 14 },
	{ label: 'Last 30 Days', days: 30 },
	{ label: 'Last 90 Days', days: 90 },
]

function TranscriptAnalysis(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { session } = controller

	// State
	const [activeTab, setActiveTab] = useState<TabValue>('overview')
	const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrameOption>(TIME_FRAME_OPTIONS[1])
	const [topWordsCount, setTopWordsCount] = useState<number>(20)
	const [topPhrasesCount, setTopPhrasesCount] = useState<number>(15)
	const [wordSortField, setWordSortField] = useState<WordSortField>('count')
	const [wordSortDirection, setWordSortDirection] = useState<SortDirection>('desc')
	const [phraseSortField, setPhraseSortField] = useState<PhraseSortField>('count')
	const [phraseSortDirection, setPhraseSortDirection] = useState<SortDirection>('desc')
	const [searchFilter, setSearchFilter] = useState<string>('')

	// Fetch transcript analysis
	const {
		data: analysisData,
		isLoading: analysisLoading,
		error: analysisError,
		refetch: refetchAnalysis,
	} = useTranscriptAnalysis(session?.user, {
		days: selectedTimeFrame.days,
		topWords: topWordsCount,
		topPhrases: topPhrasesCount,
	})

	// Fetch comparison data (only when comparison tab is active)
	const {
		data: comparisonData,
		isLoading: comparisonLoading,
		error: comparisonError,
	} = useTranscriptComparison(
		session?.user,
		{
			limit: 200,
		},
		activeTab === 'comparison'
	)

	const isLoading = analysisLoading || (activeTab === 'comparison' && comparisonLoading)
	const error = analysisError || comparisonError

	// Memoised sorted and filtered words
	const sortedWords = useMemo(() => {
		return [...(analysisData?.topWords || [])].sort((a, b) => {
			const multiplier = wordSortDirection === 'asc' ? 1 : -1
			if (wordSortField === 'word') return multiplier * a.word.localeCompare(b.word)
			return multiplier * ((a[wordSortField] || 0) - (b[wordSortField] || 0))
		})
	}, [analysisData?.topWords, wordSortField, wordSortDirection])

	const filteredWords = useMemo(() => {
		return searchFilter
			? sortedWords.filter(w => w.word.toLowerCase().includes(searchFilter.toLowerCase()))
			: sortedWords
	}, [sortedWords, searchFilter])

	// Memoised sorted and filtered phrases
	const sortedPhrases = useMemo(() => {
		return [...(analysisData?.topPhrases || [])].sort((a, b) => {
			const multiplier = phraseSortDirection === 'asc' ? 1 : -1
			if (phraseSortField === 'phrase') return multiplier * a.phrase.localeCompare(b.phrase)
			return multiplier * ((a[phraseSortField] || 0) - (b[phraseSortField] || 0))
		})
	}, [analysisData?.topPhrases, phraseSortField, phraseSortDirection])

	const filteredPhrases = useMemo(() => {
		return searchFilter
			? sortedPhrases.filter(p => p.phrase.toLowerCase().includes(searchFilter.toLowerCase()))
			: sortedPhrases
	}, [sortedPhrases, searchFilter])

	// Handle word sort
	const handleWordSort = (field: WordSortField) => {
		if (wordSortField === field) {
			setWordSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setWordSortField(field)
			setWordSortDirection('desc')
		}
	}

	// Handle phrase sort
	const handlePhraseSort = (field: PhraseSortField) => {
		if (phraseSortField === field) {
			setPhraseSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setPhraseSortField(field)
			setPhraseSortDirection('desc')
		}
	}

	// Memoised chart data
	const wordChartData = useMemo(() => {
		return analysisData?.charts?.wordBarChart
			? {
					labels: analysisData.charts.wordBarChart.slice(0, 10).map(d => d.label),
					datasets: [
						{
							label: 'Word Count',
							color: 'info' as const,
							data: analysisData.charts.wordBarChart.slice(0, 10).map(d => d.value),
						},
					],
			  }
			: null
	}, [analysisData?.charts?.wordBarChart])

	const phraseChartData = useMemo(() => {
		return analysisData?.charts?.phraseBarChart
			? {
					labels: analysisData.charts.phraseBarChart.slice(0, 8).map(d => d.label),
					datasets: [
						{
							label: 'Phrase Count',
							color: 'primary' as const,
							data: analysisData.charts.phraseBarChart.slice(0, 8).map(d => d.value),
						},
					],
			  }
			: null
	}, [analysisData?.charts?.phraseBarChart])

	// Memoised max counts for distribution bars
	const maxWordCount = useMemo(
		() => analysisData?.topWords?.[0]?.count || 1,
		[analysisData?.topWords]
	)
	const maxPhraseCount = useMemo(
		() => analysisData?.topPhrases?.[0]?.count || 1,
		[analysisData?.topPhrases]
	)

	// Memoised comparison chart data
	const userChartData = useMemo(() => {
		if (!comparisonData?.userAnalysis?.topWords) return null
		return {
			labels: comparisonData.userAnalysis.topWords.slice(0, 10).map(w => w.word),
			datasets: [
				{
					label: 'User Words',
					color: 'info' as const,
					data: comparisonData.userAnalysis.topWords.slice(0, 10).map(w => w.count),
				},
			],
		}
	}, [comparisonData?.userAnalysis?.topWords])

	const agentChartData = useMemo(() => {
		if (!comparisonData?.agentAnalysis?.topWords) return null
		return {
			labels: comparisonData.agentAnalysis.topWords.slice(0, 10).map(w => w.word),
			datasets: [
				{
					label: 'Agent Words',
					color: 'primary' as const,
					data: comparisonData.agentAnalysis.topWords.slice(0, 10).map(w => w.count),
				},
			],
		}
	}, [comparisonData?.agentAnalysis?.topWords])

	return (
		<Card id="transcript-analysis">
			<MDBox p={3} lineHeight={1}>
				{/* Header */}
				<MDBox
					display="flex"
					flexDirection={{ xs: 'column', md: 'row' }}
					justifyContent="space-between"
					alignItems={{ xs: 'flex-start', md: 'center' }}
					gap={2}
					mb={3}
				>
					<MDBox>
						<MDBox mb={1}>
							<MDTypography variant="h5">Transcript Analysis</MDTypography>
						</MDBox>
						<MDTypography variant="button" color="text" fontWeight="regular">
							Explore words and phrases from call transcripts
						</MDTypography>
					</MDBox>
					<MDBox display="flex" gap={2} alignItems="center">
						<Autocomplete
							disableClearable
							value={selectedTimeFrame}
							options={TIME_FRAME_OPTIONS}
							getOptionLabel={option => option.label}
							onChange={(_, newValue) => setSelectedTimeFrame(newValue)}
							size="small"
							sx={{ width: '10rem' }}
							renderInput={params => <MDInput {...params} label="Time Frame" />}
						/>
						<MDButton
							variant="outlined"
							color="info"
							size="small"
							onClick={() => refetchAnalysis()}
							disabled={isLoading}
						>
							<Icon sx={{ mr: 0.5 }}>refresh</Icon>
							Refresh
						</MDButton>
					</MDBox>
				</MDBox>

				{/* Tabs */}
				<MDBox mb={3}>
					<Tabs
						value={activeTab}
						onChange={(_, newValue) => {
							setActiveTab(newValue)
							setSearchFilter('')
						}}
						textColor="primary"
						indicatorColor="primary"
					>
						<Tab
							value="overview"
							label="Overview"
							icon={<Icon>dashboard</Icon>}
							iconPosition="start"
						/>
						<Tab value="words" label="Words" icon={<Icon>text_fields</Icon>} iconPosition="start" />
						<Tab
							value="phrases"
							label="Phrases"
							icon={<Icon>format_quote</Icon>}
							iconPosition="start"
						/>
						<Tab
							value="comparison"
							label="User vs Agent"
							icon={<Icon>compare_arrows</Icon>}
							iconPosition="start"
						/>
					</Tabs>
				</MDBox>

				{/* Content */}
				{isLoading && activeTab !== 'comparison' ? (
					<LoadingComponent type="box" height={400} />
				) : error ? (
					<MDBox p={3} textAlign="center">
						<MDTypography variant="body2" color="error">
							Error loading analysis data. Please try again.
						</MDTypography>
						<MDButton
							variant="outlined"
							color="error"
							size="small"
							sx={{ mt: 2 }}
							onClick={() => refetchAnalysis()}
						>
							Retry
						</MDButton>
					</MDBox>
				) : (
					<MDBox>
						{activeTab === 'overview' && (
							<OverviewTab
								analysisData={analysisData}
								wordChartData={wordChartData}
								phraseChartData={phraseChartData}
							/>
						)}
						{activeTab === 'words' && (
							<WordsTab
								words={filteredWords}
								maxWordCount={maxWordCount}
								searchFilter={searchFilter}
								onSearchFilterChange={setSearchFilter}
								topWordsCount={topWordsCount}
								onTopWordsCountChange={setTopWordsCount}
								sortField={wordSortField}
								sortDirection={wordSortDirection}
								onSort={handleWordSort}
							/>
						)}
						{activeTab === 'phrases' && (
							<PhrasesTab
								phrases={filteredPhrases}
								maxPhraseCount={maxPhraseCount}
								searchFilter={searchFilter}
								onSearchFilterChange={setSearchFilter}
								topPhrasesCount={topPhrasesCount}
								onTopPhrasesCountChange={setTopPhrasesCount}
								sortField={phraseSortField}
								sortDirection={phraseSortDirection}
								onSort={handlePhraseSort}
							/>
						)}
						{activeTab === 'comparison' && (
							<ComparisonTab
								comparisonData={comparisonData}
								isLoading={comparisonLoading}
								userChartData={userChartData}
								agentChartData={agentChartData}
							/>
						)}
					</MDBox>
				)}
			</MDBox>
		</Card>
	)
}

export default TranscriptAnalysis
